import { SearchBarContainerWithProviders } from '@eventbrite/consumer-search.search-bar';
import { IconButton, MagnifyingGlassIcon, TicketIcon, UserIcon } from '@eventbrite/marmalade';
import { SearchBarLayout } from '../ConsumerHeaderSearchBar';
import classNames from 'classnames';
import React, { useState } from 'react';
import { GlobalNavProvider, useGlobalNavContext } from '../../state';
import { getSigninOrSignupUrl, updateQueryStringParameter } from '../../utils';
import { Logo, MobileDropdown, NavLink } from '../common';
import { IntentToggle } from '../common/components/IntentToggle';
import { NavItemList } from '../common/components/NavItemList';
import { GlobalNavProps, NavItemType } from '../../types';
import styles from './GlobalNav.module.scss';
import { trackClickWithElementID } from '../../utils/tracking';
import { useIntl, defineMessage } from 'react-intl';
const METRIC_NAME_CLICK = 'GlobalNav - Logged out - Click - Search Bar (Web)';

export const GlobalNavContent = () => {
    const { navigation, env, positionSticky, searchBar, intentToggle, user } =
        useGlobalNavContext();
    const { formatMessage } = useIntl();

    const { desktop } = navigation ?? {};
    const stickyDesktopClass = positionSticky?.desktop ? 'desktopSticky' : '';
    const stickyMobileClass = positionSticky?.mobile ? 'mobileSticky' : '';
    const navClasses = classNames(styles.navContainer, stickyDesktopClass, stickyMobileClass);

    const { mobile } = navigation ?? {};
    const dropdownItems = mobile as NavItemType[];

    const [isSearchExpanded, setSearchExpanded] = useState(false);

    const mainClassnames = classNames(styles.main, styles.withSearchDropdown, {
        [styles.mobileSearchExpanded as string]: isSearchExpanded,
    });

    const findMyTicketsUrl = () => {
        return updateQueryStringParameter(
            env?.signupUrl ?? '/signin/signup',
            'referrer',
            '/mytickets',
        );
    };

    return (
        <header className={mainClassnames} data-testid="global-nav-header">
            <nav
                aria-label={formatMessage({
                    id: 'Main Navigation',
                    defaultMessage: 'Main Navigation',
                })}
                data-testid="global-nav"
                className={navClasses}
            >
                <div className={styles.navWrapper}>
                    <div className={styles.logo} tabIndex={-1}>
                        <Logo />
                    </div>
                    <SearchBarLayout
                        variant="globalNav"
                        wrapperProps={{
                            className: styles.searchBarSlot,
                            'data-testid': 'search-bar-wrapper',
                            onClick: () => {
                                trackClickWithElementID(METRIC_NAME_CLICK, 'search-bar-wrapper');
                            },
                        }}
                    >
                        <SearchBarContainerWithProviders
                            usesLanguageSubdirectory={
                                env?.localeInfo?.uses_language_subdirectory || false
                            }
                            locale={env?.localeInfo?.locale}
                            initialQuery={searchBar?.searchQuery}
                            chatbotEnabled={searchBar?.chatbotEnabled}
                            user={user}
                            searchButtonClickedExtraHandler={() => {
                                trackClickWithElementID(METRIC_NAME_CLICK, 'search-button');
                            }}
                            completionTypes={searchBar?.completionTypes}
                            onAutocompleteCallback={searchBar?.onAutocompleteCallback}
                            pageType={searchBar?.pageType}
                        />
                    </SearchBarLayout>
                    <div className={styles.mobileNavLinks}>
                        <div className={styles.collapsedSearchOnly}>
                            <IconButton
                                icon={<MagnifyingGlassIcon size="small" />}
                                aria-label={
                                    isSearchExpanded
                                        ? formatMessage({
                                              id: 'Close search',
                                              defaultMessage: 'Close search',
                                          })
                                        : formatMessage({
                                              id: 'Open search',
                                              defaultMessage: 'Open search',
                                          })
                                }
                                onClick={() => setSearchExpanded(!isSearchExpanded)}
                                className={styles.searchTriggerButton}
                                data-testid="global-nav-search-trigger"
                            />
                        </div>
                        {intentToggle && intentToggle.show ? (
                            <IntentToggle
                                activeOption={intentToggle.activeOption}
                                dropdownItems={dropdownItems}
                                isMobile
                                hostEventsRedirectURL={intentToggle.hostEventsRedirectURL}
                            />
                        ) : (
                            <ul className={styles.mobileNavLinkContainer}>
                                <li className={styles.mobileNavListLink}>
                                    <NavLink
                                        heapIdentifier={'find-my-tickets'}
                                        translatedText={defineMessage({
                                            id: 'Find my tickets',
                                            defaultMessage: 'Find my tickets',
                                        })}
                                        path={findMyTicketsUrl()}
                                        className={styles.mobileLogin}
                                        iconType={<TicketIcon size="small" />}
                                    />
                                </li>
                                <li className={styles.mobileNavListLink}>
                                    <NavLink
                                        heapIdentifier={'login'}
                                        translatedText={defineMessage({
                                            id: 'Sign in',
                                            defaultMessage: 'Sign in',
                                        })}
                                        path={getSigninOrSignupUrl(env?.signinUrl)}
                                        className={styles.mobileLogin}
                                        iconType={<UserIcon size="small" />}
                                    />
                                </li>
                                <MobileDropdown dropdownItems={dropdownItems} />
                            </ul>
                        )}
                    </div>
                    <div className={styles.inlineLinks}>
                        {intentToggle && intentToggle.show ? (
                            <IntentToggle
                                activeOption={intentToggle.activeOption}
                                dropdownItems={dropdownItems}
                                hostEventsRedirectURL={intentToggle.hostEventsRedirectURL}
                            />
                        ) : (
                            <>
                                <NavItemList
                                    inSearchDropdown
                                    navItems={desktop}
                                    serverUrl={env?.serverUrl ?? ''}
                                    signinUrl={getSigninOrSignupUrl(env?.signinUrl)}
                                    signupUrl={getSigninOrSignupUrl(env?.signupUrl)}
                                    findMyTicketsUrl={findMyTicketsUrl()}
                                />
                                <ul className={styles.inlineDropdown}>
                                    <MobileDropdown dropdownItems={dropdownItems} />
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </nav>
        </header>
    );
};

export const GlobalNav: React.FC<GlobalNavProps> = ({
    positionSticky,
    searchBar,
    logo,
    navigation,
    user,
    env,
    intentToggle,
}) => {
    return (
        <GlobalNavProvider
            positionSticky={positionSticky}
            searchBar={searchBar}
            logo={logo}
            user={user}
            env={env}
            navigation={navigation}
            intentToggle={intentToggle}
        >
            <GlobalNavContent />
        </GlobalNavProvider>
    );
};
